import { useEffect, useState } from "react";
import axios from "axios";
import { Search, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FAQPageSEO, PageSEO } from "../../components/seo";
import WZ2025Viewer from "../WZ2025Viewer";
import Layout from "../components/Layout";

interface SemanticResult {
  code: string;
  description_de: string;
  description_en?: string;
}

type ArticleBlock =
  | { type: "heading"; text: string }
  | { type: "subheading"; text: string }
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] }
  | {
      type: "table";
      headers: string[];
      rows: string[][];
      codeColumn?: number;
      accentRows?: number[];
      striped?: boolean;
    };

type FaqItem = { question: string; answer: string };

const ArticleContent = ({ blocks }: { blocks: ArticleBlock[] }) => (
  <div className="mt-16">
    {blocks.map((block, blockIndex) => {
      const key = `${block.type}-${blockIndex}`;

      if (block.type === "heading") {
        return (
          <h2
            key={key}
            className="mb-8 mt-16 text-2xl font-bold first:mt-0 dark:text-white"
          >
            {block.text}
          </h2>
        );
      }

      if (block.type === "subheading") {
        return (
          <h3
            key={key}
            className="mb-4 mt-8 text-xl font-semibold dark:text-white"
          >
            {block.text}
          </h3>
        );
      }

      if (block.type === "paragraph") {
        return (
          <p key={key} className="mb-6 leading-relaxed dark:text-white">
            {block.text}
          </p>
        );
      }

      if (block.type === "list") {
        return (
          <ul
            key={key}
            className="mb-8 list-disc space-y-2 pl-6 dark:text-white"
          >
            {block.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        );
      }

      return (
        <div key={key} className="mb-8 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-800">
                {block.headers.map((header) => (
                  <th
                    key={header}
                    className="border border-gray-200 px-4 py-2 text-left font-semibold dark:border-gray-700 dark:text-white"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="dark:text-white">
              {block.rows.map((row, rowIndex) => {
                const isAccent = block.accentRows?.includes(rowIndex);
                const isStriped = block.striped && rowIndex % 2 === 1;
                return (
                  <tr
                    key={`${row[0]}-${rowIndex}`}
                    className={
                      isAccent
                        ? "bg-energetic-mint/5"
                        : isStriped
                          ? "bg-gray-50 dark:bg-gray-800/50"
                          : undefined
                    }
                  >
                    {row.map((cell, cellIndex) => (
                      <td
                        key={`${cellIndex}-${cell}`}
                        className={`border border-gray-200 px-4 py-2 dark:border-gray-700 ${block.codeColumn === cellIndex ? "font-mono font-semibold" : ""} ${isAccent && block.codeColumn === cellIndex ? "text-energetic-mint" : ""}`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    })}
  </div>
);

const WZ2025Page = () => {
  const { t, i18n } = useTranslation("website-classifications");
  const currentYear = new Date().getFullYear();
  const interfaceLanguage = (i18n.resolvedLanguage ?? i18n.language).split(
    "-",
  )[0];
  const taxonomyLanguage: "de" | "en" =
    interfaceLanguage === "de" ? "de" : "en";
  const placeholders = t("common.placeholders", {
    returnObjects: true,
  }) as string[];
  const hierarchyItems = t("wz.info.hierarchy", {
    returnObjects: true,
  }) as string[];
  const articleBlocks = t("wz.article", {
    returnObjects: true,
  }) as ArticleBlock[];
  const faqItems = t("wz.faq.items", { returnObjects: true }) as FaqItem[];
  const [heroQuery, setHeroQuery] = useState("");
  const [heroResults, setHeroResults] = useState<SemanticResult[]>([]);
  const [heroLoading, setHeroLoading] = useState(false);
  const [heroError, setHeroError] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [highlightCode, setHighlightCode] = useState<string | null>(null);

  useEffect(() => {
    setPlaceholderIndex(0);
    const interval = window.setInterval(() => {
      setPlaceholderIndex((index) => (index + 1) % placeholders.length);
    }, 3000);
    return () => window.clearInterval(interval);
  }, [placeholders.length, interfaceLanguage]);

  const handleSearch = async () => {
    if (!heroQuery.trim()) return;
    setHeroLoading(true);
    setHeroError(false);
    setHasSearched(true);
    try {
      const response = await axios.get<{ results: SemanticResult[] }>(
        "https://hub.webmetic.de/classifications/wz2025/search",
        { params: { q: heroQuery, top_k: 3 } },
      );
      setHeroResults(response.data.results);
    } catch {
      setHeroResults([]);
      setHeroError(true);
    } finally {
      setHeroLoading(false);
    }
  };

  const resultDescription = (result: SemanticResult) =>
    taxonomyLanguage === "de"
      ? result.description_de
      : (result.description_en ?? result.description_de);

  return (
    <Layout>
      <PageSEO
        title={t("wz.seo.title", { year: currentYear })}
        description={t("wz.seo.description")}
        keywords={t("wz.seo.keywords", { returnObjects: true }) as string[]}
        path="/wz-2025-codes"
        openGraph={{
          title: t("wz.seo.openGraph.title"),
          description: t("wz.seo.openGraph.description"),
        }}
        twitter={{
          title: t("wz.seo.twitter.title"),
          description: t("wz.seo.twitter.description"),
        }}
      />
      <FAQPageSEO faqs={faqItems} />

      <section className="relative overflow-hidden bg-gradient-to-br from-graphite via-gray-900 to-black px-4 py-20">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,230,160,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(0,230,160,0.08)_1px,transparent_1px)] bg-[size:50px_50px]" />
        <div className="absolute left-1/4 top-0 h-96 w-96 animate-pulse rounded-full bg-energetic-mint/10 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 h-96 w-96 animate-pulse rounded-full bg-pulse/10 blur-3xl" />

        <div className="relative mx-auto flex max-w-6xl flex-col items-start gap-12 lg:flex-row">
          <div className="lg:w-2/5">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-energetic-mint/10 px-3 py-1 text-sm font-medium text-energetic-mint">
              <Sparkles aria-hidden="true" size={14} />
              {t("common.aiSearch")}
            </div>
            <h1 className="mb-6 font-headline text-5xl font-bold leading-tight text-white">
              {t("wz.hero.titleFirst")}
              <br />
              <span className="text-energetic-mint">
                {t("wz.hero.titleCode")}
              </span>
            </h1>
            <p className="mb-8 text-lg text-stone">
              {t("wz.hero.subtitleFirst")}
              <br />
              {t("common.semanticSearchSubtitle")}
            </p>
            <div className="flex gap-6 text-sm text-white/60">
              {(
                [
                  ["983", t("common.stats.subclasses")],
                  ["22", t("common.stats.sections")],
                  ["87", t("common.stats.divisions")],
                ] as const
              ).map(([value, label], index) => (
                <div
                  key={label}
                  className={
                    index === 0 ? undefined : "border-l border-white/20 pl-6"
                  }
                >
                  <span className="block text-2xl font-bold text-white">
                    {value}
                  </span>
                  {label}
                </div>
              ))}
            </div>
          </div>

          <div className="w-full lg:w-3/5">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
              <label
                htmlFor="wz-semantic-search"
                className="mb-3 block text-sm text-white/60"
              >
                {t("common.companyActivityLabel")}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    id="wz-semantic-search"
                    type="text"
                    placeholder={placeholders[placeholderIndex]}
                    aria-label={t("wz.hero.searchAria")}
                    className="w-full rounded-lg bg-white p-4 pl-12 text-lg text-graphite focus:outline-none focus:ring-2 focus:ring-energetic-mint"
                    value={heroQuery}
                    onChange={(event) => setHeroQuery(event.target.value)}
                    onKeyDown={(event) =>
                      event.key === "Enter" && handleSearch()
                    }
                  />
                  <Search
                    aria-hidden="true"
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-carbon"
                    size={20}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={heroLoading || !heroQuery.trim()}
                  className="rounded-lg bg-energetic-mint px-6 font-semibold text-graphite transition-colors hover:bg-energetic-mint-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t("common.search")}
                </button>
              </div>

              {heroLoading && (
                <div
                  role="status"
                  aria-label={t("common.searching")}
                  className="mt-6 flex justify-center"
                >
                  <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-energetic-mint" />
                </div>
              )}

              {!heroLoading && heroResults.length > 0 && (
                <div className="mt-6 space-y-2 text-left">
                  {heroResults.map((result) => (
                    <button
                      type="button"
                      key={result.code}
                      className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-left backdrop-blur-md transition-colors hover:bg-white/15"
                      onClick={() => {
                        setHighlightCode(null);
                        requestAnimationFrame(() =>
                          setHighlightCode(result.code),
                        );
                      }}
                    >
                      <span className="font-mono shrink-0 font-bold text-energetic-mint">
                        {result.code}
                      </span>
                      <span className="text-white">
                        {resultDescription(result)}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {!heroLoading && heroError && (
                <p className="mt-6 text-red-200">{t("common.searchError")}</p>
              )}
              {!heroLoading &&
                !heroError &&
                hasSearched &&
                heroResults.length === 0 && (
                  <p className="mt-6 text-white/60">{t("common.noResults")}</p>
                )}
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto py-8">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="mb-3 text-2xl font-bold dark:text-white">
              {t("wz.directory.title")}
            </h2>
            <p className="max-w-3xl text-carbon dark:text-gray-300">
              {t("wz.directory.description")}
            </p>
          </div>
          <WZ2025Viewer highlightCode={highlightCode} />

          <div className="mt-12 rounded-lg border border-gray-200 bg-gray-50 p-6 dark:bg-gray-900">
            <h2 className="mb-4 text-xl font-semibold dark:text-white">
              {t("wz.info.title")}
            </h2>
            <p className="mb-4 dark:text-white">{t("wz.info.description")}</p>
            <p className="mb-4 dark:text-white">
              {t("wz.info.hierarchyIntro")}
            </p>
            <ul className="mb-4 list-disc space-y-2 pl-5 dark:text-white">
              {hierarchyItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="dark:text-white">{t("wz.info.uses")}</p>
          </div>

          <ArticleContent blocks={articleBlocks} />

          <section className="mt-16">
            <h2 className="mb-6 text-2xl font-bold dark:text-white">
              {t("wz.faq.title")}
            </h2>
            <div className="space-y-6">
              {faqItems.map((item) => (
                <div key={item.question}>
                  <h3 className="mb-2 text-lg font-semibold dark:text-white">
                    {item.question}
                  </h3>
                  <p className="leading-relaxed dark:text-white">
                    {item.answer}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default WZ2025Page;
